import {
  forwardRef, useEffect, useImperativeHandle, useState,
} from 'react';

import { Dropdown } from '@rrexpress/core';
import { get, isArray, isEmpty } from 'lodash';

import { useApi } from '@/hooks/useApi';
import { IQueryFieldProps } from '@/modules/apps/model/form/query-field';
import { IClonedKitbirdDropdownOptionItem } from '@/modules/apps/model/kitbird/dropdown';

export const QueryField = forwardRef((props: IQueryFieldProps, ref) => {
  const {
    endpoint,
    endpointMethod,
    keyResponseData = 'data',
    keyOptionValue,
    keyOptionLabel,
    multi = false,
    disabled,
    keyQueryFilter,
    queryFilter = {},
    defaultOptions,
    dataProviderName,
    // ...rest
  } = props;

  const [options, setOptions] = useState<Record<string, any>[]>([]);
  const [item, setItem] = useState<IClonedKitbirdDropdownOptionItem | IClonedKitbirdDropdownOptionItem[]>(
    props.multi ? [] : {} as IClonedKitbirdDropdownOptionItem,
  );
  const {
    sendBasicRequest,
  } = useApi(dataProviderName || 'default');

  function generateQueryFilter(key: string | string[], value: string) {
    if (isArray(key)) {
      const result: Record<string, any> = {};

      for (const field of key) {
        result[field] = value;
      }

      return { ...result, ...queryFilter };
    }
    return { [key]: value, ...queryFilter };
  }

  async function querySearch(query: string) {
    const filter = {
      filter: !isEmpty(keyQueryFilter)
        ? generateQueryFilter(keyQueryFilter as string | string[], query)
        : {},
      sort: {},
    };
    const response = await sendBasicRequest(
      `/${endpoint}`,
      endpointMethod || 'POST',
      filter,
    );

    let newOptions = [{ label: '', value: '', record: {} }];

    if (defaultOptions?.length) {
      newOptions = defaultOptions as {
        label: string;
        value: string;
        record: Record<string, any>
      }[];
    }

    if (response && get(response[keyResponseData] as Record<string, any>[], 'length') > 0) {
      const optionFromResponse = response[keyResponseData].map((resp: any) => ({
        label: typeof keyOptionLabel === 'function' ? keyOptionLabel(resp) : resp[keyOptionLabel],
        value: resp[keyOptionValue],
        record: resp,
      }));

      newOptions = newOptions.concat(optionFromResponse);
    }

    setOptions(newOptions);
  }

  function onItemSelected(newItem: IClonedKitbirdDropdownOptionItem) {
    if (multi) {
      const isItemExistIndex = (item as IClonedKitbirdDropdownOptionItem[])
        .findIndex((v) => v.label === newItem.label && v.value === newItem.value);

      let newItems = [...(item as IClonedKitbirdDropdownOptionItem[])];
      if (isItemExistIndex > -1) {
        if (!newItem.checked) {
          newItems = newItems.filter((_, index) => index !== isItemExistIndex);
        }
      } else {
        newItems = [...(item as IClonedKitbirdDropdownOptionItem[]), newItem];
      }

      setItem(newItems);
      props?.onSelected?.(newItems);
    } else {
      props?.onSelected?.(newItem);
      setItem(newItem);
    }
  }

  useEffect(() => {
    querySearch('');
  }, []);

  useImperativeHandle(ref, () => ({
    getOptions: () => options,
  }));

  return (
    <Dropdown
      id={props.id ? props.id : 'query-field'}
      type="normal"
      value={props?.value ? props.value : ''}
      options={options as any[]}
      onSelected={onItemSelected}
      onSearch={(query) => querySearch(query)}
      placeholder="Search"
      label={props?.label || ''}
      multi={multi}
      disabled={disabled}
      error={props?.error}
      required={props?.required}
    />
  );
});

QueryField.displayName = 'QueryField';

export default QueryField;
